import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const instancesPag = createPaginationModule('ACTIVITY_INSTANCES');

const state = {
    instances: [],
    pagination: defaultPagination(),
    loading: false,
    error: null,
    auditLogs: [],
};

const mutations = {
    ...instancesPag.mutations,

    SET_INSTANCES(state, instances) {
        state.instances = instances;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_AUDIT_LOGS(state, logs) {
        state.auditLogs = logs;
    },
};

const actions = {
    // ✅ Fetch all instances
    async fetchInstances({ commit }, { activityId, startdate, rangeMonths = 12, search = "", page = 1, pageSize = 10 }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "getinstances",
                startdate: startdate,
                activityid: activityId,
                range_months: rangeMonths,
                search,
                page_size: pageSize,
                page_number: page,
            });
            const instances = response.data?.data?.instances || [];
            const pagination = response.data?.data?.pagination;

            if (pagination) {
                commit(instancesPag.SET_PAGINATION, {
                    currentPage: pagination.current_page,
                    totalPages: pagination.total_pages,
                    totalRecords: pagination.total_records,
                    pageSize: pagination.page_size,
                    hasPrevious: pagination.has_previous,
                    hasNext: pagination.has_next,
                });
            }

            commit("SET_INSTANCES", instances);
            return instances;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instances");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // ✅ Add new instance
    async addInstance({ commit }, payload) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "addinstance",
                ...payload,
            });
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to add instance");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // ✅ Delete specific instance
    async deleteInstance({ commit }, { locationId, instanceId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "deleteinstance",
                locationid: locationId,
                instanceid: instanceId,
            });
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to delete instance");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // ✅ Delete all future instances
    async deleteFutureInstances({ commit }, { locationId, activityId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "deletefutureinstances",
                locationid: locationId,
                activityid: activityId,
            });
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to delete future instances");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // ✅ Reset Last Instance Date
    async resetLastInstanceDate({ commit }, { activityId, locationId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "resetlastinstancedate",
                activityid: activityId,
                locationid: locationId,
            });
            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to reset last instance date");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // ✅ Get Audit Logs for Instance
    async fetchAuditLogs({ commit }, ainstanceId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax/", {
                cmd: "getAIauditlogs",
                ainstanceid: ainstanceId,
            });

            const logs = response.data?.data?.logs || [];
            commit("SET_AUDIT_LOGS", logs);
            return logs;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch audit logs");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    instances: (state) => state.instances,
    instancesPagination: (state) => state.instancesPagination,
    instancesLoading: (state) => state.loading,
    instancesError: (state) => state.error,
    auditLogs: (state) => state.auditLogs,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
